import React, { useState, useEffect } from 'react';

const NoContactClientsHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/no-contact-clients/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Historial cargado:', data);
        setHistory(data);
      } else if (response.status === 404) {
        console.log('No se encontró historial para este registro');
        setHistory([]);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setError(error.message);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    const icons = {
      'created': '➕',
      'updated': '✏️',
      'status_changed': '🔄',
      'comment_added': '💬',
      'contact_attempted': '📞',
      'email_sent': '📧',
      'meeting_scheduled': '📅',
      'follow_up': '🔔',
      'resolved': '✅',
      'escalated': '⬆️',
      'cancelled': '❌'
    };
    return icons[action] || '📝';
  };

  const getActionLabel = (action) => {
    const labels = {
      'created': 'Registro creado',
      'updated': 'Registro actualizado',
      'status_changed': 'Estado cambiado',
      'comment_added': 'Comentario agregado',
      'contact_attempted': 'Intento de contacto',
      'email_sent': 'Email enviado',
      'meeting_scheduled': 'Reunión programada',
      'follow_up': 'Seguimiento',
      'resolved': 'Resuelto',
      'escalated': 'Escalado',
      'cancelled': 'Cancelado'
    };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = {
      'created': 'bg-green-100 text-green-800',
      'updated': 'bg-blue-100 text-blue-800',
      'status_changed': 'bg-yellow-100 text-yellow-800',
      'comment_added': 'bg-purple-100 text-purple-800',
      'contact_attempted': 'bg-orange-100 text-orange-800',
      'email_sent': 'bg-indigo-100 text-indigo-800',
      'meeting_scheduled': 'bg-teal-100 text-teal-800',
      'follow_up': 'bg-pink-100 text-pink-800',
      'resolved': 'bg-green-100 text-green-800',
      'escalated': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">📊 Historial de Acciones</h2>
                <p className="text-purple-100 mt-1">Cargando historial...</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
              >
                ×
              </button>
            </div>
          </div>
          <div className="p-12 flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-lg text-gray-600">Cargando historial...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">📊 Historial de Acciones</h2>
              <p className="text-purple-100 mt-1">
                Registro completo de actividades y cambios
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error al cargar el historial
              </h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={loadHistory}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Reintentar
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Sin historial de acciones
              </h3>
              <p className="text-gray-500">
                No hay acciones registradas para este cliente aún.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Timeline */}
              <div className="relative">
                {/* Línea vertical del timeline */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
                {history.map((item, index) => (
                  <div key={item.id || index} className="relative flex items-start space-x-4 pb-8">
                    {/* Icono del timeline */}
                    <div className={`relative z-10 flex items-center justify-center w-16 h-16 rounded-full border-4 border-white shadow-lg ${getActionColor(item.action_type)}`}>
                      <span className="text-2xl">
                        {getActionIcon(item.action_type)}
                      </span>
                    </div>
                    
                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        {/* Header de la acción */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getActionColor(item.action_type)}`}>
                              {getActionIcon(item.action_type)} {getActionLabel(item.action_type)}
                            </span>
                            {item.user_name && (
                              <span className="text-sm text-gray-500">
                                por {item.user_name}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatDate(item.created_at)}
                          </span>
                        </div>
                        
                        {/* Descripción */}
                        {item.description && (
                          <p className="text-gray-700 mb-3">
                            {item.description}
                          </p>
                        )}
                        
                        {/* Detalles adicionales */}
                        {item.details && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Detalles:</h4>
                            <div className="text-sm text-gray-600">
                              {typeof item.details === 'string' ? (
                                <p>{item.details}</p>
                              ) : (
                                <pre className="whitespace-pre-wrap font-mono text-xs">
                                  {JSON.stringify(item.details, null, 2)}
                                </pre>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Cambios de estado */}
                        {item.old_status && item.new_status && (
                          <div className="flex items-center space-x-2 mt-3 text-sm">
                            <span className="text-gray-500">Estado:</span>
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              {item.old_status}
                            </span>
                            <span className="text-gray-400">→</span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {item.new_status}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {history.length > 0 ? (
              `${history.length} ${history.length === 1 ? 'acción registrada' : 'acciones registradas'}`
            ) : (
              'Sin acciones registradas'
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoContactClientsHistory;
